import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import quizApi from '../utils/quizApi';
import '../styles/Quiz.css';

const STATE_PILL = {
  miss: ['qz-pill-miss', 'No task'],
  open: ['qz-pill-open', 'To sign'],
  done: ['qz-pill-done', 'Signed'],
  na: ['qz-pill-draft', 'Not yours'],
  unknown: ['qz-pill-draft', 'Unclear'],
};

// The short headline on a locked quiz. The backend sends the full sentence in
// `blocked_message`; this is the label beside it — and it says WHY, because a bare
// "Locked" is the version of this screen that generates a support ticket.
const LOCK_LABEL = {
  open: 'Sign the task first',
  miss: 'No task assigned',
  na: 'Not assigned to you',
  unknown: 'Can’t be confirmed',
  no_snapshot: 'Report not built',
  no_profile: 'You’re not in the report',
  not_assigned: 'Not assigned to you',
};

const FILTERS = [
  ['todo', 'To do'],
  ['done', 'Done'],
  ['all', 'Everything'],
];

// The SECOND axis. The quiz list sits under the task list, and on a real trainee's
// screen the task list is dozens of rows long — so a quiz waiting to be sat is
// below the fold and gets missed. This narrows the page to one list without
// touching what "To do" means: the two filters are independent, and a chip's count
// is always the number of rows it actually yields under the other one.
const KINDS = [
  ['both', 'Tasks & quizzes'],
  ['tasks', 'Training tasks'],
  ['quizzes', 'Quizzes'],
];

const rowId = (item, kind) => `${kind}-${item.doc_key}-${item.revision}`;

/**
 * **My to-do** — the trainee's own screen, and the only one in this module written
 * for the person being trained rather than the person auditing them.
 *
 * TWO lists, because they are two different actions in two different places:
 *
 *   **Training tasks** — read the document and sign the task IN ORCANOS. This app
 *   never signs on anyone's behalf; it only reports what the training report says.
 *   **Quizzes** — sat here, and only once the matching task is signed. A quiz taken
 *   before the reading tests nothing about a document the person may never have
 *   opened, and produces the one artifact this module must never produce: a pass
 *   with no read-and-understand behind it.
 *
 * Splitting them is what makes each list actionable — one is "go to Orcanos", the
 * other is "answer questions here". They stay joined by the DOCUMENT: every quiz
 * row links to its task and every task row links to its quiz, so neither list is a
 * dead end and the order between them stays visible. A locked quiz is SHOWN rather
 * than hidden for exactly that reason — it is what tells somebody why signing
 * matters, and its primary button is the task that unlocks it.
 *
 * The lock is not enforced here. `start_attempt` enforces it (`quiz_engine.
 * sit_gate`); this screen only declines to draw a button it knows would be refused,
 * and says why.
 *
 * Three list states have to be told apart, because an empty list means something
 * different in each:
 *
 *   ok           — a real list (possibly genuinely empty: nothing owed)
 *   no_snapshot  — nobody has built the report yet
 *   no_profile   — we could not find this person in it
 *
 * Showing "you're all up to date" for the last two would be the worst thing this
 * screen could do, so each says plainly what happened and who can fix it.
 *
 * `isHome` is set when this screen IS the Training module's front door — a viewer's
 * home (App.jsx). It only drops the "← Training" button, which would otherwise
 * navigate to this very page.
 */
function MyTraining({ isHome = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useState('todo');
  const [kind, setKind] = useState('both');
  const [flash, setFlash] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const flashTimer = useRef(null);
  const alive = useRef(true);
  const started = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await quizApi.myTraining());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Re-check MY OWN training tasks against Orcanos.
   *
   * The snapshot behind this screen is rebuilt by a QA manager — but the trainee is
   * the one person whose action changes it. Somebody who signs a task in Orcanos and
   * comes straight back was shown "To sign" until the next build: a false finding,
   * shown to the only person who already knew it was wrong. This asks the server to
   * re-read the task filter (one source, a few seconds) and correct the states.
   *
   * It runs as a SECOND request after the cached one so the list renders instantly
   * and is corrected in place — a screen that waits several seconds before showing
   * anything is the worse trade. A failure here is not an error: the cached list is
   * still on screen, and the banner says it wasn't re-checked and why.
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const fresh = await quizApi.myTraining(true);
      if (alive.current) setData(fresh);
    } catch (e) {
      if (alive.current) {
        setData((d) => (d ? { ...d, refresh: { checked: false, reason: e.message } } : d));
      }
    } finally {
      if (alive.current) setRefreshing(false);
    }
  }, []);

  // `alive` is re-armed on every mount, not just set once: React's dev StrictMode
  // mounts, unmounts and mounts again, and an `alive` left false by that first
  // teardown would silently discard the live result on the mount the user sees.
  // `started` is what keeps the (multi-second) live read to one per mount.
  useEffect(() => {
    alive.current = true;
    if (!started.current) {
      started.current = true;
      load().then(refresh);
    }
    return () => { alive.current = false; };
  }, [load, refresh]);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const openQuiz = async (item) => {
    setBusy(item.doc_key);
    setError('');
    try {
      const d = await quizApi.startAttempt(item.quiz.id);
      navigate(`/training/attempt/${d.attempt.id}`);
    } catch (e) {
      // A 409 here is the sequence gate: the state moved between the page load and
      // the click. Re-check LIVE rather than re-reading the cache — the gate itself
      // re-read Orcanos before refusing, so a cached reload would redraw the very
      // button that was just refused.
      setError(e.message);
      if (e.status === 409) refresh();
    } finally {
      setBusy('');
    }
  };

  /** Jump between the two lists — the link that keeps neither of them a dead end. */
  const jumpTo = (item, target) => {
    // The target can be filtered off the screen — the other list hidden by the kind
    // chips, or the row itself excluded by To do / Done. A cross-link that silently
    // does nothing is worse than no cross-link, so relax whatever is hiding it and
    // scroll on the next tick, after that re-render.
    const wantKind = target === 'task' ? 'tasks' : 'quizzes';
    if (kind !== 'both' && kind !== wantKind) setKind('both');
    if (!keep(target === 'task' ? taskOpen(item) : quizOpen(item))) setFilter('all');

    const id = rowId(item, target);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlash(id);
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(''), 1800);
    }, 0);
  };

  const items = useMemo(() => data?.items || [], [data]);

  // "Outstanding" is judged per ROW, not per document: a signed document whose quiz
  // is still to sit is unfinished work, and burying it under Done is how it is missed.
  const taskOpen = (i) => i.training_state === 'miss' || i.training_state === 'open';
  const quizOpen = (i) => ['new', 'resume', 'locked'].includes(i.quiz?.state);

  const keep = (open) => (filter === 'all' ? true : (filter === 'todo' ? open : !open));

  const tasks = items.filter((i) => keep(taskOpen(i)));
  const quizzes = items.filter((i) => i.quiz).filter((i) => keep(quizOpen(i)));
  const withQuiz = items.filter((i) => i.quiz);

  const todoCount = items.filter(taskOpen).length + withQuiz.filter(quizOpen).length;
  const doneCount = items.filter((i) => !taskOpen(i)).length
    + withQuiz.filter((i) => !quizOpen(i)).length;

  if (loading) return <div className="qz-page"><div className="qz-loading">Loading…</div></div>;

  const state = data?.state;
  const summary = data?.summary || {};
  const live = data?.refresh || {};

  const docLine = (item) => (
    <div className="qz-task-meta">
      <span className="qz-key">{item.doc_key}</span>
      {item.revision ? <span>revision {item.revision}</span> : null}
      {item.src === 'formal' && <span className="qz-pill qz-pill-formal">Formal</span>}
      {/* Assigned to you in Orcanos AFTER the report was built — so it is real work,
          but the QA report will only show it once somebody rebuilds. Said plainly
          rather than blended in, because the two views legitimately disagree. */}
      {item.new_since_build && (
        <span className="qz-pill qz-pill-open" title="Assigned since the training report was built.">
          New
        </span>
      )}
      {item.role ? <span>· {item.role}</span> : null}
      {item.approved_on
        ? <span>· {item.date_kind === 'completed' ? 'completed' : 'approved'} {item.approved_on}</span>
        : null}
      {item.task_due ? <span>· due {item.task_due}</span> : null}
    </div>
  );

  return (
    <div className="qz-page">
      {!isHome && (
        <button className="qz-back" onClick={() => navigate('/training')}>← Training</button>
      )}

      <div className="qz-head">
        <div>
          <h2>My to-do</h2>
          <p className="qz-sub">
            Your training in the order it happens: read the document and sign its task in
            Orcanos, then take the quiz that follows. Taken from the training report — it
            shows the same picture your QA manager sees.
          </p>
        </div>
      </div>

      {error && <div className="qz-note qz-note-err"><span>{error}</span></div>}

      {/* WHEN this picture was taken — and how much of it is live.
          The list is built from the cached training report, which only a QA manager
          rebuilds. Your OWN task states are re-read from Orcanos on every visit, so a
          signature made a minute ago shows here immediately; everything else (which
          documents name your role, which revision is current) is as of the build.
          Saying which is which is the whole point of this banner: a stale "To sign"
          against a task the person has signed reads as the app losing their
          signature, and that is the one impression a compliance tool cannot afford. */}
      {state === 'ok' && (
        <div className={`qz-note ${live.checked ? 'qz-note-ok' : 'qz-note-info'}`}>
          <span>{refreshing ? '⏳' : live.checked ? '✓' : '🕒'}</span>
          <div>
            {refreshing ? (
              <>Checking Orcanos for your latest signatures…</>
            ) : live.checked ? (
              <>
                Your task states were read from Orcanos just now
                {live.changed ? <> — <b>{live.changed} updated</b></> : null}
                {live.added ? <>, {live.added} newly assigned</> : null}.
                {' '}The rest of this list is from the training report built{' '}
                <b>{data.built_at}</b>.{live.error ? <> {live.error}</> : null}
              </>
            ) : (
              <>
                Taken from the training report built <b>{data.built_at}</b>.{' '}
                {live.reason || live.error
                  || 'Your own tasks could not be re-checked against Orcanos just now, so a '
                     + 'signature made since that build may not show yet.'}
              </>
            )}
            {!refreshing && (
              <>
                {' '}
                <button className="qz-linkbtn" onClick={refresh}>Check again</button>
              </>
            )}
          </div>
        </div>
      )}

      {state === 'no_snapshot' && (
        <div className="qz-empty">
          <b>The training report hasn’t been built yet</b>
          <span>
            This screen reads the training panel’s report. Until someone builds it, there is
            nothing to show — which is <b>not</b> the same as having no training to do.
            Ask your training manager to build the report.
          </span>
        </div>
      )}

      {state === 'no_profile' && (
        <div className="qz-empty">
          <b>We couldn’t find you in the training report</b>
          <span>
            You’re signed in as <b>{data.user_id}</b>, but no user profile in the report matches
            that id{data.profiles_seen ? ` (${data.profiles_seen} profiles were checked)` : ''}.
            That usually means the User Profile filter doesn’t include you, or your profile’s
            <b> Assigned To</b> is a different id. <b>This is not the same as owing no training</b> —
            please ask your training manager to check.
          </span>
        </div>
      )}

      {state === 'ok' && (
        <>
          <div className="qz-kpis">
            <div className="qz-kpi due">
              <div className="v">{summary.due || 0}</div>
              <div className="l">Tasks to sign</div>
              <div className="s">In Orcanos</div>
            </div>
            <div className="qz-kpi quiz">
              <div className="v">{summary.quizzes_open || 0}</div>
              <div className="l">Quizzes to take</div>
              <div className="s">
                {summary.quizzes_locked
                  ? `${summary.quizzes_locked} more once signed`
                  : 'Open to you now'}
              </div>
            </div>
            <div className="qz-kpi ok">
              <div className="v">{(summary.signed || 0) + (summary.quizzes_passed || 0)}</div>
              <div className="l">Done</div>
              <div className="s">Signed &amp; passed</div>
            </div>
          </div>

          {items.length > 0 && (
            <div className="qz-toolbar">
              {FILTERS.map(([k, label]) => (
                <button
                  key={k}
                  className={`qz-chip${filter === k ? ' active' : ''}`}
                  onClick={() => setFilter(k)}
                >
                  {label}
                  <span className="qz-chip-n">
                    {k === 'all' ? todoCount + doneCount : (k === 'todo' ? todoCount : doneCount)}
                  </span>
                </button>
              ))}

              {/* Which list. Only offered when there is a quiz to be missed — with
                  none attached, these chips would be three ways of seeing the same
                  page. The counts are taken AFTER the To do / Done filter above, so
                  each chip promises exactly the rows it will show. */}
              {withQuiz.length > 0 && (
                <>
                  {/* Styled inline rather than as a class: Quiz.css is shared with
                      the other quiz screens and every stylesheet here is global
                      (Critical Note #15), so a one-off 1px rule earns no name. */}
                  <span
                    aria-hidden="true"
                    style={{
                      width: 1, alignSelf: 'stretch', minHeight: 18,
                      background: 'var(--gray-200, #E8E5F2)', margin: '0 4px',
                    }}
                  />
                  {KINDS.map(([k, label]) => (
                    <button
                      key={k}
                      className={`qz-chip${kind === k ? ' active' : ''}`}
                      onClick={() => setKind(k)}
                    >
                      {label}
                      <span className="qz-chip-n">
                        {k === 'both'
                          ? tasks.length + quizzes.length
                          : (k === 'tasks' ? tasks.length : quizzes.length)}
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {items.length === 0 ? (
            <div className="qz-empty">
              <b>Nothing outstanding</b>
              <span>No document in the training report names a role you hold.</span>
            </div>
          ) : (
            <>
              {/* -------------------------------------------- ① training tasks */}
              {kind !== 'quizzes' && (
              <div className="qz-section">
                <div className="qz-section-head">
                  <h3>Training tasks</h3>
                  <span className="qz-section-sub">
                    Read the document, then sign the task in Orcanos — this app never signs
                    for you.
                  </span>
                  <span className="qz-count">{tasks.length}</span>
                </div>

                {tasks.length === 0 ? (
                  <div className="qz-section-empty">
                    {filter === 'todo'
                      ? 'Every training task on your list is signed.'
                      : 'Nothing here yet.'}
                  </div>
                ) : tasks.map((item) => {
                  const [pillCls, pillLabel] = STATE_PILL[item.training_state] || STATE_PILL.unknown;
                  const quiz = item.quiz;
                  const id = rowId(item, 'task');
                  return (
                    <div id={id} key={id}
                         className={`qz-row ${item.training_state}${flash === id ? ' flash' : ''}`}>
                      <div className="qz-row-main">
                        <div className="qz-task-title">{item.doc_name || item.doc_key}</div>
                        {docLine(item)}
                        {/* The forward half of the cross-link: what this signature
                            unlocks, said on the row that unlocks it. */}
                        {quiz && (
                          <div className="qz-row-foot">
                            {quiz.state === 'locked'
                              ? <>🔒 A quiz follows — it opens once this task is signed.</>
                              : quiz.state === 'passed'
                                ? <>✓ Quiz passed with {quiz.score_pct}%.</>
                                : (
                                  <>
                                    Quiz open:&nbsp;
                                    <button className="qz-linkbtn" onClick={() => jumpTo(item, 'quiz')}>
                                      {quiz.title || 'take it below'} ↓
                                    </button>
                                  </>
                                )}
                          </div>
                        )}
                      </div>
                      <div className="qz-row-right">
                        <span className={`qz-pill ${pillCls}`}>{pillLabel}</span>
                        {item.doc_url && (
                          <a className="qz-btn qz-btn-ghost qz-btn-sm" href={item.doc_url}
                             target="_blank" rel="noopener noreferrer">
                            Document ↗
                          </a>
                        )}
                        {item.task_url ? (
                          <a className={`qz-btn qz-btn-sm ${item.training_state === 'done' ? 'qz-btn-ghost' : 'qz-btn-primary'}`}
                             href={item.task_url} target="_blank" rel="noopener noreferrer">
                            Open task ↗
                          </a>
                        ) : (
                          <span className="qz-row-note">Nothing to sign</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              {/* -------------------------------------------------- ② quizzes */}
              {kind !== 'tasks' && (
              <div className="qz-section">
                <div className="qz-section-head">
                  <h3>Quizzes</h3>
                  <span className="qz-section-sub">
                    Taken here. Each one opens once its training task is signed.
                  </span>
                  <span className="qz-count">{quizzes.length}</span>
                </div>

                {quizzes.length === 0 ? (
                  <div className="qz-section-empty">
                    {withQuiz.length === 0
                      ? 'None of your documents has a quiz attached.'
                      : (filter === 'todo'
                        ? 'No quiz is waiting for you.'
                        : 'You haven’t finished a quiz yet.')}
                  </div>
                ) : quizzes.map((item) => {
                  const quiz = item.quiz;
                  const id = rowId(item, 'quiz');
                  return (
                    <div id={id} key={id}
                         className={`qz-row quiz ${quiz.state}${flash === id ? ' flash' : ''}`}>
                      <div className="qz-row-main">
                        <div className="qz-task-title">{quiz.title || `${item.doc_key} quiz`}</div>
                        {docLine(item)}
                        <div className="qz-row-foot">
                          {quiz.state === 'locked' || quiz.state === 'exhausted'
                            ? quiz.blocked_message
                            : quiz.state === 'passed'
                              ? `Passed with ${quiz.score_pct}%.`
                              : `${quiz.questions} question${quiz.questions === 1 ? '' : 's'} · `
                                + `pass mark ${quiz.pass_mark}% · ${quiz.attempts_remaining} of `
                                + `${quiz.attempts_allowed} attempt${quiz.attempts_allowed === 1 ? '' : 's'} left`}
                        </div>
                      </div>

                      <div className="qz-row-right">
                        {quiz.state === 'passed' && (
                          <>
                            <span className="qz-pill qz-pill-done">{quiz.score_pct}%</span>
                            {quiz.record_state === 'pending' && (
                              <span className="qz-pill qz-pill-open"
                                    title="Your pass is recorded here and is waiting to reach Orcanos.">
                                Recording…
                              </span>
                            )}
                          </>
                        )}

                        {quiz.state === 'locked' && (
                          <span className="qz-pill qz-pill-lock">
                            🔒 {LOCK_LABEL[quiz.lock_reason] || 'Locked'}
                          </span>
                        )}

                        {quiz.state === 'exhausted' && (
                          <span className="qz-pill qz-pill-miss">No attempts left</span>
                        )}

                        {/* The back half of the cross-link. On a LOCKED row this is the
                            action, not a courtesy: the task is what unlocks the quiz, so
                            sending somebody straight to it is the whole answer. */}
                        {item.task_url ? (
                          <a
                            className={`qz-btn qz-btn-sm ${quiz.state === 'locked' ? 'qz-btn-primary' : 'qz-btn-ghost'}`}
                            href={item.task_url} target="_blank" rel="noopener noreferrer"
                          >
                            {quiz.state === 'locked' ? 'Sign the task ↗' : 'Task ↗'}
                          </a>
                        ) : (
                          <button className="qz-btn qz-btn-ghost qz-btn-sm"
                                  onClick={() => jumpTo(item, 'task')}>
                            See the task ↑
                          </button>
                        )}

                        {(quiz.state === 'new' || quiz.state === 'resume') && (
                          <button
                            className="qz-btn qz-btn-primary qz-btn-sm"
                            disabled={busy === item.doc_key}
                            onClick={() => openQuiz(item)}
                          >
                            {quiz.state === 'resume' ? 'Resume quiz' : 'Start quiz'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default MyTraining;
